/**
 * Orientation of a map PDF. Independent of the PDF type: a location PDF exists in both
 * orientations, while the types without a map crop (overview, traffic, nearby) have none at all.
 * The value is sent as the `orientation` query parameter.
 */
export const PdfOrientation = {
    HORIZONTAL: "horizontal",
    VERTICAL: "vertical",
} as const;

// Value and type intentionally share the name (a valid TS value/type merge);
// the lint rule doesn't model this and would flag it as a redeclaration.
export type PdfOrientation = (typeof PdfOrientation)[keyof typeof PdfOrientation];

/**
 * The map/location PDFs REVA can render. The value is the last path segment of the download URL
 * and matches the corresponding reva-pdf endpoint (`/overview-pdf`, `/micro-location-pdf`, …).
 * {@link MICRO_LOCATION}, {@link MACRO_LOCATION} and {@link CADASTRE} are the ones offered in both
 * {@link PdfOrientation}s.
 */
export const LocationPdfType = {
    OVERVIEW: "overview",
    LOCATION: "location",
    TRAFFIC: "traffic",
    NEARBY: "nearby",
    MICRO_LOCATION: "micro-location",
    MACRO_LOCATION: "macro-location",
    CADASTRE: "cadastre",
} as const;

// Value and type intentionally share the name (a valid TS value/type merge);
// the lint rule doesn't model this and would flag it as a redeclaration.
export type LocationPdfType = (typeof LocationPdfType)[keyof typeof LocationPdfType];

/**
 * The three valuation reports REVA can render, for the live valuation as well as for a stored
 * snapshot. The value is the last path segment of the download URL and the document type the
 * (pre-)finalization archives (see {@link import("../api/payload-types/persistence-finalize").ArchiveDocumentType}).
 */
export const ValuationPdfType = {
    FULL_VALUATION: "full-valuation",
    VALUATION: "valuation",
    SIMPLE_VALUATION: "simple-valuation",
} as const;

// Value and type intentionally share the name (a valid TS value/type merge);
// the lint rule doesn't model this and would flag it as a redeclaration.
export type ValuationPdfType = (typeof ValuationPdfType)[keyof typeof ValuationPdfType];

/** Logical pixel size of one map crop. */
export interface PdfMapPixelSize {
    width: number;
    height: number;
}

/**
 * Logische Pixelmaße der Karten-Ausschnitte je PDF-Export/Orientierung.
 * Diese Größen bestimmen das Seitenverhältnis der roten Vorschau-Box auf der
 * interaktiven Karte (kein devicePixelRatio einrechnen). Keys passend zu
 * {@link PdfOrientation}; alles ohne Orientierung (Overview, Traffic, Nearby)
 * nutzt {@code default}.
 */
export const PDF_MAP_PIXEL_SIZE: Record<PdfOrientation | "default", PdfMapPixelSize> = {
    horizontal: {width: 1500, height: 680},
    // Diese Maße müssen exakt zu den Maßen in reva-pdf (getMapImage /
    // location-pdf-services) passen — die Vorschau-Box zeigt den Ausschnitt 1:1
    // (unskaliert) und ragt bei zu kleinem Viewport bewusst über den Kartenrand.
    vertical: {width: 900, height: 1080},
    default: {width: 900, height: 1080},
};

/**
 * Constant for the gross floor area model document property key.
 */
export const DOC_PROPERTY_GROSS_FLOOR_AREA_MODEL = "gross_floor_area_model";

/**
 * Constant for the pois per type document property key.
 */
export const DOC_PROPERTY_POIS_PER_TYPE = "pois_per_type";
